/**
 * Campaign and Flight Types
 * For marketing campaign management with multi-channel distribution
 */

import type { BaseEntity, UUID } from "./common.types";
import type { URLParameters } from "./voucherTemplate.types";
import type { CampaignSelectionMode, TargetAudience } from "./program.types";

// Campaign status
export type CampaignStatus = "draft" | "active" | "completed" | "cancelled";

// Campaign type. 'marketing' is legacy/unspecified — operators flip
// new and existing rows between 'journey' (multi-step drip series)
// and 'single_send' (one-off blast) via the form toggle. 'event_ticket'
// and 'event_reminder' are system-controlled by the events flow and
// stay read-only in the form.
export type CampaignType =
  | "marketing"
  | "journey"
  | "single_send"
  | "event_ticket"
  | "event_reminder";

// Flight status
export type FlightStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "cancelled"
  | "failed";

// Campaign entity
export interface Campaign extends BaseEntity {
  // Basic information
  name: string;
  description?: string;

  // Campaign type
  campaign_type: CampaignType;
  /** Optional SendGrid reporting category applied to every flight in this campaign. */
  sendgrid_category?: string | null;
  event_id?: UUID;

  // Timing
  start_date?: string;
  end_date?: string;

  // Status
  status: CampaignStatus;

  // Metadata
  metadata?: Record<string, unknown>;

  // Statistics (denormalized)
  total_flights: number;
  total_vouchers_generated: number;
  total_vouchers_redeemed: number;

  // Incentive Program Framework fields
  program_id?: UUID;
  selection_mode?: CampaignSelectionMode;
  max_selections?: number;
  target_audience?: TargetAudience;
  /** Soft delete (migration 203). NULL/false = visible. Set only by the soft_delete_campaign RPC. */
  is_deleted?: boolean | null;
  deleted_at?: string | null;
  deleted_by?: UUID | null;

  // Audit
  created_by?: UUID;
}

// Campaign flight entity
export interface CampaignFlight extends BaseEntity {
  campaign_id: UUID;
  /**
   * Set only by Event in a Box. Its presence means the row is a launch-owned
   * invitation: the database permits exactly one operator transition on it
   * (scheduled → cancelled) and no field edits.
   */
  eib_launch_id?: UUID | null;
  /** EIB flight role: 'invite', 'invite_reminder', … (null for ordinary flights). */
  eib_flight_role?: string | null;

  // Flight identification
  name: string;
  description?: string;

  // Targeting
  list_id?: UUID;

  // Voucher configuration
  template_id?: UUID; // legacy voucher_templates link; vouchers now derive from offer
  generate_vouchers?: boolean; // issue a voucher per recipient from the linked offer
  // Reminder flights (Fix E): when set, reuses this source flight's vouchers + list; mints nothing.
  voucher_source_flight_id?: UUID | null;
  // Optional client annotation (not a DB column): reusable vouchers already minted by this
  // flight, used to qualify reminder source candidates whose generate_vouchers flag is stale.
  reusable_voucher_count?: number | null;
  parameter_overrides?: URLParameters;

  // Offer association
  offer_id?: UUID;
  email_template_registry_id?: UUID;

  // Email configuration (Sendgrid)
  email_subject?: string;
  email_template_id?: string; // Sendgrid template ID
  subscription_group_id?: UUID | null; // ASM email subscription category
  /** Delivery IP pool key: 'default' (current shared pool) or 'usls_email_pool_01' (warmup).
   *  Server resolves the key against a fixed catalog; a raw SendGrid pool name is never accepted. */
  sendgrid_ip_pool_key?: string | null;
  email_template_data?: Record<string, unknown>;

  // SMS configuration (internal)
  sms_template_id?: UUID;
  sms_message_override?: string;

  // Scheduling
  scheduled_send_at?: string;
  sent_at?: string;
  lease_id?: UUID | null;
  lease_expires_at?: string | null;
  worker_started_at?: string | null;
  send_recipients_snapshot_complete_at?: string | null;
  send_snapshot_progress?: Record<string, unknown> | null;

  // Status
  status: FlightStatus;

  // Statistics (denormalized)
  total_recipients: number;
  vouchers_generated: number;

  // Email stats
  emails_sent: number;
  emails_delivered: number;
  emails_opened: number;
  emails_clicked: number;
  emails_bounced: number;
  emails_deferred: number;

  // SMS stats
  sms_sent: number;
  sms_delivered: number;
  sms_failed: number;

  // Voucher stats
  vouchers_redeemed: number;

  // Metadata
  metadata?: Record<string, unknown>;

  // Audit
  created_by?: UUID;
}

// SMS Template
export interface SMSTemplate extends BaseEntity {
  name: string;
  description?: string;
  body: string;
  variables: string[]; // Array of variable names like ["voucher_code", "location_name"]
  character_count: number;
  created_by?: UUID;
  deleted_at?: string;
}

// Email Tracking
export interface EmailTracking extends BaseEntity {
  flight_id: UUID;
  contact_id?: UUID;
  email_address: string;

  // Sendgrid tracking
  sendgrid_message_id?: string;

  // Status events
  sent_at?: string;
  delivered_at?: string;
  opened_at?: string;
  clicked_at?: string;
  bounced_at?: string;
  bounce_reason?: string;
  bounce_type?: "hard" | "soft" | "spam";
  unsubscribed_at?: string;

  // Click tracking
  clicked_links?: Array<{ url: string; clicked_at: string }>;

  // Metadata
  metadata?: Record<string, unknown>;
}

// SMS Tracking
export interface SMSTracking extends BaseEntity {
  flight_id: UUID;
  contact_id?: UUID;
  phone_number: string;

  // Provider tracking
  provider_message_id?: string;

  // Message
  message_body: string;

  // Status events
  sent_at?: string;
  delivered_at?: string;
  failed_at?: string;
  failure_reason?: string;

  // Cost tracking
  segments_used?: number;

  // Metadata
  metadata?: Record<string, unknown>;
}

// Campaign Performance (from view)
export interface CampaignPerformance {
  campaign_id: UUID;
  campaign_name: string;
  status: CampaignStatus;
  start_date?: string;
  end_date?: string;
  total_flights: number;
  total_vouchers: number;
  redeemed_vouchers: number;
  redemption_rate_pct: number;
  total_emails_sent: number;
  total_emails_opened: number;
  total_sms_sent: number;
  created_at: string;
  updated_at: string;
}

// Email Template Registry
export interface EmailTemplateRegistry {
  id: UUID;
  sendgrid_template_id: string;
  name: string;
  description?: string;
  supports_voucher: boolean;
  supports_multi_offer: boolean;
  merge_fields: string[];
  category?:
    | "marketing"
    | "transactional"
    | "event"
    | "reminder"
    | "multi_offer";
  preview_url?: string;
  thumbnail_url?: string;
  metadata?: Record<string, unknown>;
  last_used_at?: string;
  usage_count: number;
  created_by?: UUID;
  created_at: string;
  updated_at: string;
}

// Flight-Offer association (for multi-offer emails)
export interface FlightOffer {
  id: UUID;
  flight_id: UUID;
  offer_id: UUID;
  display_order: number;
  display_name?: string;
  display_description?: string;
  slot_key?: string;
  created_at: string;
}

// DTOs
export interface CreateCampaignDTO {
  name: string;
  description?: string;
  campaign_type?: CampaignType;
  sendgrid_category?: string | null;
  event_id?: UUID;
  start_date?: string;
  end_date?: string;
  metadata?: Record<string, unknown>;
  // Incentive Program Framework fields
  program_id?: UUID;
  selection_mode?: CampaignSelectionMode;
  max_selections?: number;
  target_audience?: TargetAudience;
}

export interface UpdateCampaignDTO {
  name?: string;
  description?: string;
  campaign_type?: CampaignType;
  sendgrid_category?: string | null;
  start_date?: string;
  end_date?: string;
  status?: CampaignStatus;
  metadata?: Record<string, unknown>;
  // Incentive Program Framework fields
  program_id?: UUID;
  selection_mode?: CampaignSelectionMode;
  max_selections?: number;
  target_audience?: TargetAudience;
}

export interface CreateFlightDTO {
  campaign_id: UUID;
  name: string;
  /**
   * Set ONLY by Event in a Box. It is the flight's identity within a launch —
   * what makes it adoptable on retry — and a database trigger keys on it to
   * refuse any attempt to schedule the invitation outside the guarded launch
   * transaction.
   */
  eib_launch_id?: UUID | null;
  /** EIB flight role: 'invite' (the invitation) or 'invite_reminder'. */
  eib_flight_role?: string | null;
  /** EIB reminder offset in days before the event (0/1/2); null for the invite. */
  eib_reminder_offset_days?: number | null;
  /** EIB reminder: the invitation flight whose sent recipients it copies. */
  recipient_source_flight_id?: UUID | null;
  description?: string;
  list_id?: UUID;
  template_id?: UUID;
  generate_vouchers?: boolean;
  // Reminder flights (Fix E): when set, this flight reuses the source flight's vouchers
  // (same codes) + its list; it generates nothing. NULL/absent = normal flight.
  voucher_source_flight_id?: UUID | null;
  parameter_overrides?: URLParameters;

  // Offer association
  offer_id?: UUID;
  email_template_registry_id?: UUID;

  // Email configuration
  email_subject?: string;
  email_template_id?: string;
  email_template_data?: Record<string, unknown>;
  // ASM email subscription category (marketing group). Null/absent = none.
  subscription_group_id?: UUID | null;
  /** Delivery IP pool key: 'default' (current shared pool) or 'usls_email_pool_01' (warmup).
   *  Server resolves the key against a fixed catalog; a raw SendGrid pool name is never accepted. */
  sendgrid_ip_pool_key?: string | null;

  // SMS configuration
  sms_template_id?: UUID;
  sms_message_override?: string;

  // Scheduling
  scheduled_send_at?: string;

  metadata?: Record<string, unknown>;
}

export interface UpdateFlightDTO {
  name?: string;
  description?: string;
  list_id?: UUID;
  template_id?: UUID;
  generate_vouchers?: boolean;
  // Reminder flights (Fix E): when set, this flight reuses the source flight's vouchers
  // (same codes) + its list; it generates nothing. NULL/absent = normal flight.
  voucher_source_flight_id?: UUID | null;
  parameter_overrides?: URLParameters;

  // Offer association
  offer_id?: UUID;
  email_template_registry_id?: UUID;

  // Email configuration
  email_subject?: string;
  email_template_id?: string;
  email_template_data?: Record<string, unknown>;
  // ASM email subscription category (marketing group). Null/absent = none.
  subscription_group_id?: UUID | null;
  /** Delivery IP pool key: 'default' (current shared pool) or 'usls_email_pool_01' (warmup).
   *  Server resolves the key against a fixed catalog; a raw SendGrid pool name is never accepted. */
  sendgrid_ip_pool_key?: string | null;

  // SMS configuration
  sms_template_id?: UUID;
  sms_message_override?: string;

  // Scheduling
  scheduled_send_at?: string;

  // Status
  status?: FlightStatus;

  metadata?: Record<string, unknown>;
}

export interface CreateSMSTemplateDTO {
  name: string;
  description?: string;
  body: string;
  variables?: string[];
}

export interface UpdateSMSTemplateDTO {
  name?: string;
  description?: string;
  body?: string;
  variables?: string[];
}

// Constants
export const CAMPAIGN_STATUS_OPTIONS = [
  {
    value: "draft" as CampaignStatus,
    label: "Draft",
    description: "Campaign being created",
  },
  {
    value: "active" as CampaignStatus,
    label: "Active",
    description: "Campaign is running",
  },
  {
    value: "completed" as CampaignStatus,
    label: "Completed",
    description: "Campaign finished",
  },
  {
    value: "cancelled" as CampaignStatus,
    label: "Cancelled",
    description: "Campaign cancelled",
  },
];

export const FLIGHT_STATUS_OPTIONS = [
  {
    value: "draft" as FlightStatus,
    label: "Draft",
    description: "Flight being created",
  },
  {
    value: "scheduled" as FlightStatus,
    label: "Scheduled",
    description: "Scheduled to send",
  },
  {
    value: "sending" as FlightStatus,
    label: "Sending",
    description: "Currently sending",
  },
  {
    value: "sent" as FlightStatus,
    label: "Sent",
    description: "Send completed",
  },
  {
    value: "cancelled" as FlightStatus,
    label: "Cancelled",
    description: "Flight cancelled",
  },
  {
    value: "failed" as FlightStatus,
    label: "Failed",
    description: "Send terminated before all recipients received",
  },
];

export const CAMPAIGN_TYPE_OPTIONS = [
  {
    value: "journey" as CampaignType,
    label: "Journey",
    description: "Multi-step drip series spread over time",
  },
  {
    value: "single_send" as CampaignType,
    label: "Single Send",
    description: "One-off email blast",
  },
  {
    value: "marketing" as CampaignType,
    label: "Marketing Campaign",
    description:
      "Legacy / unspecified — re-categorize as Journey or Single Send",
  },
  {
    value: "event_ticket" as CampaignType,
    label: "Event Tickets",
    description: "Send tickets/QR codes to event attendees",
  },
  {
    value: "event_reminder" as CampaignType,
    label: "Event Reminder",
    description: "Send reminders to event attendees",
  },
];

// Subset of CampaignType values the operator can pick via the form
// toggle. Excludes the event-* values which are system-controlled.
export const OPERATOR_TOGGLEABLE_CAMPAIGN_TYPES: ReadonlyArray<CampaignType> = [
  "journey",
  "single_send",
];
